import React from 'react';
import EffectorBase, { EffectorPropertyDrawer } from "./EffectorBase";
import DropdownProperty from "../../../components/properties/DropdownProperty";
import StepProperty from '../../../components/properties/StepProperty';
import AssetProperty from '../../../components/properties/AssetProperty';
import { PropertyTypes, CustomEventTypes, AssetTypes } from "../../../globals";
import Engine from '../../Engine';
import Scenario from '../../scenarioElements/Scenario';
import Utils from "../../Utilities";
import TextUtils from '../../utilities/TextUtils';
import {ObjectTriggerTypes, VisibilityModes} from "../../../interfaces/enums";
import MaterialAsset from "../../MaterialAsset";
import ARTrackerObjectProperty from '../../../components/properties/ARTrackerObjectProperty';
import store, { StoreActions } from '../../../state/store';
import SceneObjectUtils from '../../utilities/SceneObjectUtils';
import SelectObjectsAction from '../../../actions/SelectObjectsAction';
import ARTracker from '../../ARTracker';
import { TimelineFragment } from '../TimelineFragment';
import { IDropdownOption } from '../../../interfaces/Misc';
import { TimelineElementTypes } from '../../../interfaces/Timeline';


interface  EffectorEventInvocationSaveData
{
	target?: ObjectTriggerTypes;
	params: any;
	customMaterial?: string;
}

export default class EffectorEventInvocation extends EffectorBase<EffectorEventInvocation>
{
	target?: ObjectTriggerTypes;
	params: any;
	fired : boolean;
	customMaterial?: string;
	
	constructor(fragment : TimelineFragment)
	{
		super(fragment);
		this.target = undefined;
		this.params = {};
		this.fired = false;
		this.customMaterial = undefined;
	}

	save = () : EffectorEventInvocationSaveData =>
	{
		const output = {
			target: this.target,
			params: this.params,
			...(this.params === VisibilityModes.Custom && { customMaterial: this.customMaterial}),
		};

		return output;
	};

	load = (savedData: EffectorEventInvocationSaveData) =>
	{
		this.target = savedData.target;
		this.params = savedData.params;
		this.customMaterial = savedData.customMaterial;
		if (this.customMaterial)
		{
			MaterialAsset.registerCustomMaterialReference(this);
		}
	};

	copyValues = (sourceEffector: EffectorEventInvocation) =>
	{
		this.target = sourceEffector.target;
		this.params = sourceEffector.params;
		this.customMaterial = sourceEffector.customMaterial;
		if (this.customMaterial)
		{
			MaterialAsset.registerCustomMaterialReference(this);
		}
	}

	resetState = () =>
	{

	}

	onSimulationStopped = () =>
	{
		this.fired = false;
	}

	fireEvent = () =>
	{
		if (!this.fired)
		{
			switch (this.target)
			{
				case ObjectTriggerTypes.VisibilityToggle:
					this.forEachTargetObject((targetObject) =>
					{
						Engine.getInstance().getTriggerManager().setVisibilityMode(targetObject, this.params, this.customMaterial);
					});
					break;
				case ObjectTriggerTypes.CalloutToggle:
					this.forEachTargetObject((targetObject) =>
					{
						Engine.getInstance().getTriggerManager().activateCalloutToggle(targetObject);
					});
					break;
				case ObjectTriggerTypes.NextStep:
					const nextStep = Scenario.currentStep()?.getNextStep();
					if (nextStep)
					{
						Scenario.setCurrentStep(nextStep);
					}
					break;
				case ObjectTriggerTypes.PreviousStep:
					const previousStep = Scenario.currentStep()?.getPreviousStep();
					if (previousStep)
					{
						Scenario.setCurrentStep(previousStep);
					}
					break;
				case ObjectTriggerTypes.MoveToStep:
					const targetStep = Scenario.getStep(this.params);
					if (targetStep)
					{
						Scenario.setCurrentStep(targetStep);
					}
					break;
				case ObjectTriggerTypes.Alignment:
					// save current step & set it as "next step" for the simulation - unless there's no "next step", in which case we don't fire the event
					const returnStep = Scenario.currentStep()?.getNextStep();
					if(!returnStep || !this.params) break;

					store.dispatch(StoreActions.ui.setNextStepOverride(returnStep.id));
					// move to CPS step
					const cpsStep = Scenario.getCPS()!;
					Scenario.setCurrentStep(cpsStep);
					// select the ar tracker - this.params can be set to a deleted tracker, so we need to check if it exists
					const tracker = SceneObjectUtils.findObjectByGuid(ARTracker.getTrackerRoot(), this.params);
					if(tracker)
					{
						new SelectObjectsAction([tracker], Engine.getInstance().getPickingManager().getCurrentSelection().objects);
					}
					break;
			}
			this.fired = true;
		}
	}

	restoreEvent = () =>
	{
		if (this.fired)
		{
			switch (this.target)
			{
				case ObjectTriggerTypes.VisibilityToggle:
					this.forEachTargetObject((targetObject) =>
					{
						Engine.getInstance().getTriggerManager().restoreVisibility(targetObject);
					});
					break;
				case ObjectTriggerTypes.CalloutToggle:
					this.forEachTargetObject((targetObject) =>
					{
						Engine.getInstance().getTriggerManager().activateCalloutToggle(targetObject);
					});
					break;
			}

			this.fired = false;
		}
	}

	update = (fragmentTime: number) =>
	{
		if (Utils.IsSimulationRunning())
		{
			if (this.fragment.previousTime > fragmentTime || fragmentTime === 0) this.restoreEvent();
			if (fragmentTime > 0 && fragmentTime < 1) 	this.fireEvent();
		}
	}

	onAdded = () =>
	{
		document.addEventListener(CustomEventTypes.simulationStopped, this.onSimulationStopped);
	}

	onDelete = () =>
	{
		super.onDelete();
		document.removeEventListener(CustomEventTypes.simulationStopped, this.onSimulationStopped);
		this.fired = false;
	}

	setCustomMaterial = (materialGuid: string) =>
	{
		if(materialGuid === MaterialAsset.defaultMaterialGuid)
		{
			this.customMaterial = undefined;
		}
		else
		{
			this.customMaterial = materialGuid;
			MaterialAsset.registerCustomMaterialReference(this);
		}
	}

	getPropertyForEvent = () =>
	{
		switch (this.target)
		{
			case ObjectTriggerTypes.VisibilityToggle:
				return ([
					new EffectorPropertyDrawer(
						"Visibility Mode",
						PropertyTypes.DropDown,
						<DropdownProperty
							data-name={"event-visibility-parameter"}
							value={this.params}
							defaultValue={DefaultParamsMap[this.target]}
							options={
								Object.entries(VisibilityModes).map(([key, value]) =>
								{
									return { display: TextUtils.prettifyText(key), value: value };
								})
							}
						/>,
						() => {return this.params;},
						(v) =>
						{
							this.params = v;
						},
					),
					...this.params === VisibilityModes.Custom ? 
						[new EffectorPropertyDrawer(
							"Custom Material",
							PropertyTypes.Asset,
							<AssetProperty
								data-name={"custom-material-parameter"}
								label={"Custom Material"}
								value={this.customMaterial}
								type={AssetTypes.Material}
							/>,
							() => { return this.customMaterial; },
							(v) =>
							{
								this.setCustomMaterial(v);
							},
						)] : [],
				]);
			case ObjectTriggerTypes.MoveToStep:
				return ([
					new EffectorPropertyDrawer(
						"Target Step",
						PropertyTypes.Step,
						<StepProperty
							data-name={"trigger-step-parameter"}
							label={"Target Step"}
							value={this.params}
							defaultValue={DefaultParamsMap[this.target]}
						/>,
						() => {return this.params;},
						(v) =>
						{
							this.params = v;
						},
					),
				]);
			case ObjectTriggerTypes.Alignment:
				return ([
					new EffectorPropertyDrawer(
						"Select Tracker",
						PropertyTypes.ARTracker,
						<ARTrackerObjectProperty
							data-name={"trigger-tracker-parameter"}
							label={"Select Tracker"}
							value={this.params}
							defaultValue={DefaultParamsMap[this.target]}
						/>,
						() => {return this.params;},
						(v) =>
						{
							this.params = v;
						},
					)
				]);
			default:
				return [];
		}
	}

	getEditorProperties = () =>
	{
		const options: IDropdownOption[] = [];
		if (this.fragment.getType() === TimelineElementTypes.ActionTrackFragment)
		{
			options.push({ display: TextUtils.prettifyText(ObjectTriggerTypes.NextStep), value: ObjectTriggerTypes.NextStep });
			options.push({ display: TextUtils.prettifyText(ObjectTriggerTypes.PreviousStep), value: ObjectTriggerTypes.PreviousStep });
			options.push({ display: TextUtils.prettifyText(ObjectTriggerTypes.MoveToStep), value: ObjectTriggerTypes.MoveToStep });
			options.push({ display: "Content Placement", value: ObjectTriggerTypes.Alignment });
		}
		else
		{
			options.push({ display: TextUtils.prettifyText(ObjectTriggerTypes.VisibilityToggle), value: ObjectTriggerTypes.VisibilityToggle });
			options.push({ display: TextUtils.prettifyText(ObjectTriggerTypes.CalloutToggle), value: ObjectTriggerTypes.CalloutToggle });
		}

		return [
			...this.getCommonPropertyDrawers(),
			new EffectorPropertyDrawer(
				"Action Type",
				PropertyTypes.DropDown,
				<DropdownProperty
					data-name={"event-type"}
					value={this.target}
					options={options}
					warning={(this.target === ObjectTriggerTypes.Alignment && !Scenario.currentStep()?.getNextStep()) ? "Alignment Triggers must be followed by another step to work" : undefined}
				/>,
				() => {return this.target;},
				(v) =>
				{
					this.params = DefaultParamsMap[v];
					this.target = v;
				},
			),
			...this.getPropertyForEvent(),
		];
	}
}

const DefaultParamsMap = {
	[ObjectTriggerTypes.VisibilityToggle]: VisibilityModes.Solid,
	[ObjectTriggerTypes.MoveToStep]: null,
	[ObjectTriggerTypes.CalloutToggle]: null,
	[ObjectTriggerTypes.Alignment]: null,
};
